import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/google-business-profile");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/google-business-profile")({
  head: () => ({
    meta: [
      {
        title: "How to Rank in the Google Maps 3-Pack in Colorado (2026 Playbook)",
      },
      {
        name: "description",
        content:
          "A step-by-step Google Business Profile playbook for Colorado service businesses to rank in the Maps 3-pack — without paying for ads.",
      },
      {
        property: "og:title",
        content: "Google Maps 3-Pack Playbook for Colorado Businesses",
      },
      {
        property: "og:description",
        content:
          "The exact GBP optimization steps we use for Colorado HVAC, plumbing, roofing, and legal clients.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/google-business-profile` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/google-business-profile" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Google Maps"
      read="10 min read"
      title="How to rank in the Google Maps 3-pack (without paying for ads)"
      intro="If you&rsquo;re not in the top 3 Google Maps results for your core service in your city, you&rsquo;re losing 60–70% of the available phone calls. Here&rsquo;s the exact playbook we use to fix that for Colorado service businesses."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Google Maps 3-pack" },
      ]}
      path="/learn/google-business-profile"
      faqs={[
        { q: "What is the most important Google Business Profile ranking factor?", a: "Proximity to the searcher, primary category, and the volume and recency of authentic 5-star reviews — in that order. Everything else (photos, posts, services) supports those three." },
        { q: "How often should I post on Google Business Profile?", a: "Once a week is the sweet spot. More than that doesn't help rankings; less than once a month signals an inactive profile to Google." },
        { q: "Can I rank in the Google Map Pack without a physical storefront?", a: "Yes — service-area businesses can rank if your address is verified, your service area is defined, and your reviews mention the cities you serve." },
      ]}
    >
      <h2>What the &ldquo;3-pack&rdquo; is and why it matters</h2>
      <p>
        Search &ldquo;plumber Colorado Springs&rdquo; right now. The three businesses with the map
        above them — that&rsquo;s the 3-pack. Roughly 65% of clicks on local searches go to those
        three listings. Position 4 and below get a fraction of the calls.
      </p>
      <p>
        The good news: ranking in the 3-pack is one of the highest-ROI moves a Colorado small
        business can make, and you do <strong>not</strong> need to buy Google Ads to get there.
      </p>

      <h2>The 3 ranking factors Google actually uses</h2>
      <ol>
        <li>
          <strong>Relevance</strong> — How well your profile matches what was searched. Driven by
          your primary category, service list, and the words on your website.
        </li>
        <li>
          <strong>Distance</strong> — How close you are to the searcher. You can&rsquo;t fake your
          address, but you <em>can</em> influence which neighborhoods you rank in.
        </li>
        <li>
          <strong>Prominence</strong> — How well-known your business is. Driven by reviews,
          citations, backlinks, and (yes) brand searches.
        </li>
      </ol>

      <h2>The step-by-step playbook</h2>

      <h3>1. Claim and verify with the right primary category</h3>
      <p>
        Your <strong>primary category</strong> is the single most important field on your profile.
        Be specific. &ldquo;HVAC contractor&rdquo; beats &ldquo;Contractor.&rdquo; &ldquo;Family
        law attorney&rdquo; beats &ldquo;Lawyer.&rdquo; Then add every relevant secondary category
        (you can have up to 9).
      </p>

      <h3>2. Fill out every services line item</h3>
      <p>
        Add each service you offer as a separate entry — &ldquo;AC repair,&rdquo; &ldquo;AC
        installation,&rdquo; &ldquo;Furnace repair,&rdquo; etc. Write a 1–2 sentence description
        for each. This is where 80% of profiles leave money on the table.
      </p>

      <h3>3. Service area or storefront — choose correctly</h3>
      <p>
        If customers come to you, list your address. If you go to them (most home services), set
        yourself as a service-area business and list specific Colorado cities/counties.{" "}
        <strong>Do not stuff 50 cities.</strong> Pick the 5–15 you actually serve.
      </p>

      <h3>4. Photos — real ones, weekly</h3>
      <p>
        Upload real photos of your team, trucks, finished jobs, and storefront. Add new photos at
        least monthly. Profiles with 100+ photos get roughly 2× the calls of profiles with under 10.
      </p>

      <h3>5. Reviews — volume, recency, and replies</h3>
      <p>
        You need a steady drip of new reviews (target: 4–8 per month minimum) and you need to
        respond to every single one — good and bad — within 48 hours. We have a separate guide on{" "}
        <Link to="/learn/get-more-reviews" className="font-bold text-brand-gold-deep underline">
          how to get 5-star reviews the right way
        </Link>
        .
      </p>

      <h3>6. Google Posts — at least 2 per month</h3>
      <p>
        Most businesses ignore Posts. They&rsquo;re free, they show in your knowledge panel, and
        they tell Google your profile is active. Post promotions, new services, completed jobs, or
        seasonal reminders.
      </p>

      <h3>7. Q&amp;A — seed your own</h3>
      <p>
        Google lets users post questions. You can post the most common ones yourself and answer
        them. Things like &ldquo;Do you offer free estimates?&rdquo; &ldquo;Do you serve Monument,
        CO?&rdquo; &ldquo;What hours are you open?&rdquo;
      </p>

      <h3>8. Citations &amp; NAP consistency</h3>
      <p>
        Your Name, Address, and Phone (NAP) must be <em>identical</em> across Yelp, BBB, Angi,
        Nextdoor, your industry directories, your Chamber listing, and your website footer. One
        wrong digit can cost you 3-pack spots.
      </p>

      <h3>9. A landing page on your site that matches</h3>
      <p>
        Your Google Business Profile&rsquo;s &ldquo;website&rdquo; link should point to a city +
        service page on your site, not your homepage. The closer the page matches what Google sees
        on your profile, the better you rank.
      </p>

      <h2>Realistic timeline</h2>
      <ul>
        <li><strong>Week 1–2:</strong> Cleanup (categories, services, NAP). Initial movement on long-tail searches.</li>
        <li><strong>Month 2–3:</strong> Photo + post + review cadence kicks in. Top-10 on most core searches.</li>
        <li><strong>Month 4–6:</strong> Top-3 placement on most core city + service searches.</li>
      </ul>

      <h2>Want us to handle it?</h2>
      <p>
        Our{" "}
        <Link to="/colorado-springs-google-maps" className="font-bold text-brand-gold-deep underline">
          Google Maps service
        </Link>{" "}
        does every step above each month, in-house, by a real Colorado team. No bots, no overseas
        outsourcing, no white-label nonsense. Pricing is on{" "}
        <Link to="/pricing" className="font-bold text-brand-gold-deep underline">
          our pricing page
        </Link>{" "}
        — no &ldquo;contact us for a quote&rdquo; games.
      </p>
    </ArticleLayout>
  );
}
